import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { collectionPath } from '../constants/shopCategories'
import { SITE_LINKS } from '../constants/siteLinks'
import { servicePath } from '../constants/servicePages'
import './HomeLanding.css'

const HERO_SLIDES = [
  {
    id: 'connect',
    label: 'Connectivity',
    icon: 'fa-wifi',
    image: '/assets/img/slider/starlink-hero-hd.jpg',
    flip: true,
    kicker: "Rwanda's tech & security partner",
    title: 'Everything to Connect, Secure and Power',
    highlight: 'Rwanda',
    text: 'Fiber and satellite internet, digital security, networking, power backup, software and training — delivered and supported by one trusted local team.',
    points: ['Fiber & Starlink internet', 'Professional installation', 'Local support'],
    cta: { label: 'Shop Starlink Kits', to: collectionPath('starlink') },
  },
  {
    id: 'secure',
    label: 'Digital Security',
    icon: 'fa-shield-halved',
    image: '/assets/img/slider/security-hero-hd.jpg',
    kicker: 'Complete digital security',
    title: 'Protect What Matters',
    highlight: 'Most',
    text: 'CCTV surveillance, access control, alarms and smart monitoring — designed and installed for homes, offices and institutions.',
    points: ['CCTV surveillance', 'Access control & alarms', 'Remote monitoring'],
    cta: { label: 'Shop Security', to: collectionPath('digital-security') },
  },
  {
    id: 'network',
    label: 'Networking & IT',
    icon: 'fa-network-wired',
    image: '/assets/img/slider/network-hero-hd.jpg',
    kicker: 'Networking & IT solutions',
    title: 'Build Reliable Business',
    highlight: 'Networks',
    text: 'Enterprise switching, Wi-Fi and structured cabling for offices, schools and enterprises — deployed by our engineers.',
    points: ['Enterprise switching', 'Office & campus Wi-Fi', 'Structured cabling'],
    cta: { label: 'Explore Networking', to: SITE_LINKS.serviceNetwork },
  },
  {
    id: 'build',
    label: 'Software & Training',
    icon: 'fa-code',
    image: '/assets/img/slider/software-hero-v2.jpg',
    kicker: 'Software & training',
    title: 'Build Software and Skills That',
    highlight: 'Grow',
    text: 'Custom websites, business systems and mobile apps — plus internships and short courses with real project work.',
    points: ['Websites & web apps', 'Business systems & mobile apps', 'Internships & short courses'],
    cta: { label: 'Explore Software', to: servicePath('software-solutions') },
  },
]

// How long each slide stays; the progress bar's CSS animation uses the same duration and its end advances the slide
const SLIDE_MS = 7000

function SlideCta({ cta, tabIndex }) {
  const content = (
    <>
      {cta.label}
      <i className="fa-solid fa-arrow-right" aria-hidden="true" />
    </>
  )
  return cta.href ? (
    <a href={cta.href} className="hm-btn hm-btn--primary" tabIndex={tabIndex}>
      {content}
    </a>
  ) : (
    <Link to={cta.to} className="hm-btn hm-btn--primary" tabIndex={tabIndex}>
      {content}
    </Link>
  )
}

/**
 * Home hero carousel: full-width photo with a light fade, headline and slide-specific actions, and a
 * bottom navigator (one tab per slide with a filling progress bar). Hover or focus pauses it; the slide
 * advances when the active tab's progress animation ends, so pausing simply freezes the bar.
 * The sections below it live in HomeHighlights.jsx.
 */
function HomeShowcase() {
  const [slide, setSlide] = useState(0)
  const [paused, setPaused] = useState(false)
  const count = HERO_SLIDES.length
  const step = (dir) => setSlide((s) => (s + dir + count) % count)

  return (
    <section
      id="home"
      className={`hm-hero${paused ? ' is-paused' : ''}`}
      aria-roledescription="carousel"
      aria-label="Highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {HERO_SLIDES.map((item, index) => {
        const active = index === slide
        return (
          <div
            key={item.id}
            className={`hm-hero__slide${active ? ' is-active' : ''}`}
            aria-hidden={!active}
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${count}: ${item.label}`}
          >
            <div className={`hm-hero__bg${item.flip ? ' is-flipped' : ''}${item.fit === 'height' ? ' is-fit-height' : ''}`} style={{ backgroundImage: `url('${item.image}')` }} />
            <div className="hm-container hm-hero__inner">
              <div className="hm-hero__content">
                <span className="hm-hero__kicker">{item.kicker}</span>
                {index === 0 ? (
                  <h1 className="hm-hero__title">
                    {item.title} <span>{item.highlight}</span>
                  </h1>
                ) : (
                  <h2 className="hm-hero__title">
                    {item.title} <span>{item.highlight}</span>
                  </h2>
                )}
                <p className="hm-hero__text">{item.text}</p>
                <ul className="hm-hero__points">
                  {item.points.map((point) => (
                    <li key={point}>
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
                <div className="hm-hero__ctas">
                  <SlideCta cta={item.cta} tabIndex={active ? 0 : -1} />
                  <a href={SITE_LINKS.contact} className="hm-btn hm-btn--outline" tabIndex={active ? 0 : -1}>
                    <i className="fa-solid fa-headset" aria-hidden="true" />
                    Free Consultation
                  </a>
                </div>
              </div>
            </div>
          </div>
        )
      })}

      <div className="hm-hero__nav">
        <div className="hm-container hm-hero__nav-inner">
          <div className="hm-hero__tabs" role="tablist" aria-label="Choose a slide">
            {HERO_SLIDES.map((item, index) => {
              const active = index === slide
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`hm-hero__tab${active ? ' is-active' : ''}`}
                  onClick={() => setSlide(index)}
                >
                  <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                  <span className="hm-hero__tab-label">{item.label}</span>
                  <span className="hm-hero__progress" aria-hidden="true">
                    {active && (
                      <span
                        key={slide}
                        style={{ animationDuration: `${SLIDE_MS}ms` }}
                        onAnimationEnd={() => step(1)}
                      />
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HomeShowcase
